import React from "react";
import { TimeSelectPropsWithFormControl } from "./types";
import { FilterableSelect } from "@cmp-src/form/FilterableSelect/FilterableSelect";
import { useTimeInputMask } from "@gettactic/helpers/src/input/useTimeInputMask";
import { generateStringTimeItems } from "@gettactic/helpers/src/date-time/generateStringTimeItems";
import { isValidTime } from "@gettactic/helpers/src/date-time/isValidTime";
import { TIME_SELECT_INTERVAL_MINUTES } from "@cmp-src/date-time/constants";

// Regex for time without AM/PM (e.g., "09:30" or "9:30")
const timeWithoutPeriodRegex = /^(0?[1-9]|1[0-2]):[0-5]\d$/;
// Regex for time with AM/PM (e.g., "09:30am", "9:30 PM", "09:30 AM")
const timeWithPeriodRegex = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*(a|am|p|pm)$/i;

/**
 * Returns custom value suggestions for time input.
 * If user types time without AM/PM, suggests both AM and PM variants.
 * If user types complete time with AM/PM, returns that single value normalized.
 */
const getTimeCustomValueSuggestions = (searchValue: string): string[] => {
  const trimmed = searchValue.trim();

  // Check if it's a time without AM/PM
  if (timeWithoutPeriodRegex.test(trimmed)) {
    // Normalize hour to 2 digits
    const [hours, minutes] = trimmed.split(":");
    const paddedHours = hours.padStart(2, "0");
    return [`${paddedHours}:${minutes} AM`, `${paddedHours}:${minutes} PM`];
  }

  // Check if it's a complete time with am/pm (from mask: "09:30am", "09:30a", "09:30 PM")
  const withPeriodMatch = trimmed.match(timeWithPeriodRegex);
  if (withPeriodMatch) {
    const paddedHours = withPeriodMatch[1].padStart(2, "0");
    const mins = withPeriodMatch[2];
    const periodInput = withPeriodMatch[3].toUpperCase();
    // Normalize: "A" -> "AM", "P" -> "PM"
    const period = periodInput.startsWith("A") ? "AM" : "PM";
    return [`${paddedHours}:${mins} ${period}`];
  }

  return [trimmed];
};

export const TimeSelect = React.forwardRef<
  HTMLInputElement,
  TimeSelectPropsWithFormControl
>((props, ref) => {
  const { interval = TIME_SELECT_INTERVAL_MINUTES, ...rest } = props;
  const timeMask = useTimeInputMask();

  const timeItems = React.useMemo(() => {
    let foundValueTime = false;
    const values = generateStringTimeItems(interval, true).map((time) => {
      if (time === rest.value) {
        foundValueTime = true;
      }
      return {
        value: time,
        label: time
      };
    });

    if (!foundValueTime && rest.value) {
      values.unshift({
        value: rest.value,
        label: rest.value
      });
    }

    return values;
  }, [interval, rest.value]);

  return (
    <FilterableSelect
      items={timeItems}
      validateCustomValue={(value) => isValidTime(value, true)}
      getCustomValueSuggestions={getTimeCustomValueSuggestions}
      mask={timeMask}
      allowCustomValue
      {...rest}
    />
  );
});

TimeSelect.displayName = "TimeSelect";
